import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { clientService } from "../../../Services/client.service";
import { errorHandling } from "../../../Services/error.handling";
import { ToastComponent } from "../../Components/ToastComponent";
import DeleteModal from "../../Components/Modals/Delete-modal";
const DAYS_OF_MONTH = Array.from({ length: 31 }, (_, i) =>
  String(i + 1).padStart(2, "0")
);

const initialFinesState = {
  fineValue: "",
  visitValue: "",
  removalValue: "",
  unproductiveVisitValue: "",
  replacementValue: "",
};

export default function CommercialCondition({ clientId }) {
  const { t } = useTranslation();

  // Card "Vencimento / Ciclo" — campos sem endpoint confirmado
  // permanecem manuais.
  const [dueDay, setDueDay] = useState("");
  const [nextDueDate, setNextDueDate] = useState("");
  const [cutoffDay, setCutoffDay] = useState("");
  const [previousCycleStart, setPreviousCycleStart] = useState("");
  const [previousCycleEnd, setPreviousCycleEnd] = useState("");
  const [currentCycleStart, setCurrentCycleStart] = useState("");
  const [currentCycleEnd, setCurrentCycleEnd] = useState("");
  const [billingValidatedByClient, setBillingValidatedByClient] = useState(false);

  // Card "Condição Comercial"
  const [conditionType, setConditionType] = useState("");
  const [planValidity, setPlanValidity] = useState("");
  const [nextReadjustment, setNextReadjustment] = useState("");
  const [conditionDescription, setConditionDescription] = useState("");

  // Combos
  const [payDays, setPayDays] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [installmentPlans, setInstallmentPlans] = useState([]);
  const [contractDurations, setContractDurations] = useState([]);
  const [contractTypes, setContractTypes] = useState([]);
  const [combosLoading, setCombosLoading] = useState(true);

  // Serviços contratados da condição comercial selecionada
  const [contractedFeatures, setContractedFeatures] = useState([]);
  const [checkedFeatures, setCheckedFeatures] = useState({});
  const [featuresLoading, setFeaturesLoading] = useState(false);

  // Valores e formas de pagamento (mapeados de CommercialTermResponse quando
  // uma condição é selecionada; ver handleEditCondition)
  const [serviceValue, setServiceValue] = useState("");
  const [serviceTotalPlanValue, setServiceTotalPlanValue] = useState("");
  const [servicePeriodId, setServicePeriodId] = useState("");
  const [serviceMonthlyValue, setServiceMonthlyValue] = useState("");
  const [servicePaymentMethodId, setServicePaymentMethodId] = useState("");
  const [serviceInstallmentId, setServiceInstallmentId] = useState("");

  const [installationValue, setInstallationValue] = useState("");
  const [installationPaymentMethodId, setInstallationPaymentMethodId] = useState("");
  const [installationInstallmentId, setInstallationInstallmentId] = useState("");

  const [equipmentValue, setEquipmentValue] = useState("");
  const [equipmentPaymentMethodId, setEquipmentPaymentMethodId] = useState("");
  const [equipmentInstallmentId, setEquipmentInstallmentId] = useState("");

  const [fines, setFines] = useState(initialFinesState);

  // Condições comerciais cadastradas (commercial-terms)
  const [registeredConditions, setRegisteredConditions] = useState([]);
  const [conditionsLoading, setConditionsLoading] = useState(true);
  const [selectedConditionId, setSelectedConditionId] = useState(null);
  const [conditionToDelete, setConditionToDelete] = useState(null);
  const [isDeactivating, setIsDeactivating] = useState(false);
  const [isSavingCondition, setIsSavingCondition] = useState(false);
  const [toastParams, setToastParams] = useState("");

  const showToast = (title, body, color) => {
    setToastParams(JSON.stringify({ show: true, title, body, color }));
  };

  useEffect(() => {
    let isMounted = true;

    async function loadCombos() {
      setCombosLoading(true);
      const [
        payDaysResult,
        paymentMethodsResult,
        installmentPlansResult,
        contractDurationsResult,
        contractTypesResult,
      ] = await Promise.all([
        clientService.fetchPayDays(),
        clientService.fetchPaymentMethods(),
        clientService.fetchInstallmentPlans(),
        clientService.fetchContractDurations(),
        clientService.fetchContractTypes(),
      ]);

      if (!isMounted) return;

      setPayDays(Array.isArray(payDaysResult) ? payDaysResult : []);
      setPaymentMethods(Array.isArray(paymentMethodsResult) ? paymentMethodsResult : []);
      setInstallmentPlans(Array.isArray(installmentPlansResult) ? installmentPlansResult : []);
      setContractDurations(Array.isArray(contractDurationsResult) ? contractDurationsResult : []);
      setContractTypes(Array.isArray(contractTypesResult) ? contractTypesResult : []);
      setCombosLoading(false);
    }

    async function loadCommercialTerms() {
      setConditionsLoading(true);
      const result = await clientService.fetchCommercialTerms(0);
      if (isMounted) {
        setRegisteredConditions(Array.isArray(result) ? result : []);
        setConditionsLoading(false);
      }
    }

    loadCombos();
    loadCommercialTerms();

    return () => {
      isMounted = false;
    };
  }, []);

  const toggleFeature = (featureId) => {
    setCheckedFeatures((prev) => ({ ...prev, [featureId]: !prev[featureId] }));
  };

  const suggestedValue = useMemo(() => {
    return contractedFeatures.reduce((total, feature) => {
      return checkedFeatures[feature.featureId] ? total + (feature.value || 0) : total;
    }, 0);
  }, [contractedFeatures, checkedFeatures]);

  const handleFinesChange = (field) => (e) => {
    setFines((prev) => ({ ...prev, [field]: e.target.value }));
  };

  // Carrega os dados de uma condição comercial já cadastrada: preenche o
  // bloco de valores/formas de pagamento e busca os serviços contratados.
  const handleEditCondition = async (condition) => {
    setSelectedConditionId(condition.commercialConditionId);

    setConditionType(condition.commercialConditionTypeId ?? "");
    setPlanValidity(condition.commercialConditionDurationId ?? "");

    setServiceValue(condition.serviceValue ?? "");
    // ASSUMIDO: nome do campo no GET commercial-terms — confirmar quando
    // testar contra um cliente real via DevTools/Network.
    setServiceTotalPlanValue(condition.servicePlanTotalValue ?? "");
    setServicePeriodId(condition.servicePeriodId ?? "");
    setServiceMonthlyValue(condition.serviceMonthlyValue ?? "");
    setServicePaymentMethodId(condition.servicePaymentMethodId ?? "");
    setDueDay(condition.serviceDueDay ? String(condition.serviceDueDay).padStart(2, "0") : "");

    setInstallationValue(condition.installationValue ?? "");
    setInstallationPaymentMethodId(condition.installationPaymentMethodId ?? "");
    setInstallationInstallmentId(condition.installationInstallmentId ?? "");

    setEquipmentValue(condition.equipmentValue ?? "");
    setEquipmentPaymentMethodId(condition.equipmentPaymentMethodId ?? "");
    setEquipmentInstallmentId(condition.equipmentInstallmentId ?? "");

    setFines({
      fineValue: condition.fineValue ?? "",
      visitValue: condition.visitValue ?? "",
      removalValue: condition.removalValue ?? "",
      unproductiveVisitValue: condition.unproductiveVisitValue ?? "",
      replacementValue: condition.replacementValue ?? "",
    });

    setFeaturesLoading(true);
    const features = await clientService.fetchContractedFeatures(
      condition.commercialConditionId
    );
    setContractedFeatures(Array.isArray(features) ? features : []);
    if (Array.isArray(features)) {
      const checked = {};
      features.forEach((feature) => {
        if (feature.inUse === "true" || feature.inUse === true) {
          checked[feature.featureId] = true;
        }
      });
      setCheckedFeatures(checked);
    }
    setFeaturesLoading(false);
  };

  const confirmDeactivateCondition = async (conditionId) => {
    setIsDeactivating(true);
    const result = await clientService.deactivateCommercialCondition(conditionId);
    setIsDeactivating(false);

if (!result || result.error) {
  const backendMessage = errorHandling.stripProcedurePrefix(
    result?.friendlyMessage || result?.message
  );
  showToast(
    t("COMMON.ERROR"),
    backendMessage || t("CUSTOMER.COMMERCIAL_CONDITION.LOAD_CATALOGS_ERROR"),
    "Danger"
  );
  return;
}

showToast(t("COMMON.SUCCESS"), t("CUSTOMER.COMMERCIAL_CONDITION.DEACTIVATED_SUCCESS"), "Success");

    setRegisteredConditions((prev) =>
      prev.filter((condition) => condition.commercialConditionId !== conditionId)
    );
    if (selectedConditionId === conditionId) {
      setSelectedConditionId(null);
    }
  };

  // TODO(confirmar): nomes PascalCase seguem o schema do Swagger
  // (POST/PUT /customers/plans/commercial-terms). Os 3 campos "...DayId"
  // assumem o mesmo valor do Dia de Vencimento do topo — não há select
  // separado por bloco na tela hoje. Description não tem campo próprio no
  // formulário; usa a descrição da condição em edição, ou vazio se for nova.
  const buildCommercialTermsPayload = () => {
    // eslint-disable-next-line no-unused-vars
    const editingCondition = registeredConditions.find(
      (c) => c.commercialConditionId === selectedConditionId
    );
    return {
      Description: conditionDescription || "",
      CommercialConditionTypeId: conditionType || null,
      CommercialConditionDurationId: planValidity || null,
      AnniversaryDate: nextDueDate || null,
      ServiceValue: serviceValue || 0,
      ServiceCommercialConditionPeriodId: servicePeriodId || null,
      ServiceMonthlyValue: serviceMonthlyValue || 0,
      ServiceCommercialConditionPaymentMethodId: servicePaymentMethodId || null,
      ServiceCommercialConditionInstallmentId: serviceInstallmentId || null,
      ServiceCommercialConditionDayId: dueDay || null,
      InstallationValue: installationValue || 0,
      InstallationCommercialConditionPaymentMethodId: installationPaymentMethodId || null,
      InstallationCommercialConditionInstallmentId: installationInstallmentId || null,
      InstallationCommercialConditionDayId: dueDay || null,
      EquipmentValue: equipmentValue || 0,
      EquipmentCommercialConditionPaymentMethodId: equipmentPaymentMethodId || null,
      EquipmentCommercialConditionInstallmentId: equipmentInstallmentId || null,
      EquipmentCommercialConditionDayId: dueDay || null,
      FineValue: fines.fineValue || 0,
      RemovalValue: fines.removalValue || 0,
      ExchangeValue: fines.replacementValue || 0,
      VisitValue: fines.visitValue || 0,
      UnproductiveVisitValue: fines.unproductiveVisitValue || 0,
    };
  };

  const handleSaveCommercialCondition = async () => {
    setIsSavingCondition(true);
    const payload = buildCommercialTermsPayload();
    const result = selectedConditionId
      ? await clientService.updateCommercialCondition(selectedConditionId, payload)
      : await clientService.createCommercialCondition(payload);
    setIsSavingCondition(false);

    if (!result || result.error) {
      const validationMessage = errorHandling.formatValidationErrors(result?.raw?.validations);
      const backendMessage = errorHandling.stripProcedurePrefix(
        result?.friendlyMessage || result?.message
      );
      showToast(
        t("COMMON.ERROR"),
        validationMessage || backendMessage || t("CUSTOMER.COMMERCIAL_CONDITION.LOAD_CATALOGS_ERROR"),
        "Danger"
      );
      return;
    }

    showToast(t("COMMON.SUCCESS"), t("CUSTOMER.PERSONAL_DATA.UPDATED_SUCCESS"), "Success");

    const savedId = result?.commercialConditionId ?? selectedConditionId;
    if (savedId) setSelectedConditionId(savedId);

    setConditionsLoading(true);
    const refreshed = await clientService.fetchCommercialTerms(0);
    setRegisteredConditions(Array.isArray(refreshed) ? refreshed : []);
    setConditionsLoading(false);
  };

  const handleSave = handleSaveCommercialCondition;
  const handleSavePaymentValues = handleSaveCommercialCondition;

  const handleClearPaymentValues = () => {
    setServiceValue("");
    setServiceTotalPlanValue("");
    setServicePeriodId("");
    setServiceMonthlyValue("");
    setServicePaymentMethodId("");
    setInstallationValue("");
    setInstallationPaymentMethodId("");
    setInstallationInstallmentId("");
    setEquipmentValue("");
    setEquipmentPaymentMethodId("");
    setEquipmentInstallmentId("");
    setFines(initialFinesState);
  };

  return (
    <div className="p-3">
      <ToastComponent
        params={toastParams}
        closeToast={() => setToastParams(JSON.stringify({ show: false }))}
      />

      {/* Vencimento / Ciclo */}
      <div className="card mb-4">
        <div className="card-header bg-dark text-white fw-semibold mb-3">
          {t("CUSTOMER.COMMERCIAL_CONDITION.CONDITION_LABEL")}
        </div>
        <div className="card-body">
          <div className="row g-3">
            <div className="col-md-3">
              <label className="form-label">
                {t("CUSTOMER.COMMERCIAL_CONDITION.DUE_DAY")}
              </label>
              <select
                className="form-select"
                value={dueDay}
                onChange={(e) => setDueDay(e.target.value)}
                disabled={combosLoading}
              >
                <option value="">
                  {t("CUSTOMER.COMMERCIAL_CONDITION.SELECT")}
                </option>
                {payDays.length > 0
                  ? payDays.map((day) => (
                      <option key={day.payDayId} value={day.payDayId}>
                        {day.payDayNumber}
                      </option>
                    ))
                  : DAYS_OF_MONTH.map((day) => (
                      <option key={day} value={day}>
                        {day}
                      </option>
                    ))}
              </select>
            </div>
            <div className="col-md-3">
              <label className="form-label">
                {t("CUSTOMER.COMMERCIAL_CONDITION.NEXT_DUE_DATE")}
              </label>
              <input
                type="date"
                className="form-control"
                value={nextDueDate}
                onChange={(e) => setNextDueDate(e.target.value)}
              />
            </div>
            {/* Sem campo em CommercialTermResponse (GAP 2) — mantido manual */}
            <div className="col-md-3">
              <label className="form-label">
                {t("CUSTOMER.COMMERCIAL_CONDITION.CUTOFF_DAY")}
              </label>
              <input
                type="number"
                min="1"
                max="31"
                className="form-control"
                value={cutoffDay}
                onChange={(e) => setCutoffDay(e.target.value)}
              />
            </div>
          </div>

          <hr className="my-3" />

          {/* Sem campos em CommercialTermResponse (GAP 2) — mantidos manuais */}
          <div className="row g-3">
            <div className="col-md-3">
              <label className="form-label">
                {t("CUSTOMER.COMMERCIAL_CONDITION.PREVIOUS_CYCLE_START")}
              </label>
              <input
                type="date"
                className="form-control"
                value={previousCycleStart}
                onChange={(e) => setPreviousCycleStart(e.target.value)}
              />
            </div>
            <div className="col-md-3">
              <label className="form-label">
                {t("CUSTOMER.COMMERCIAL_CONDITION.PREVIOUS_CYCLE_END")}
              </label>
              <input
                type="date"
                className="form-control"
                value={previousCycleEnd}
                onChange={(e) => setPreviousCycleEnd(e.target.value)}
              />
            </div>
            <div className="col-md-3">
              <label className="form-label">
                {t("CUSTOMER.COMMERCIAL_CONDITION.CURRENT_CYCLE_START")}
              </label>
              <input
                type="date"
                className="form-control"
                value={currentCycleStart}
                onChange={(e) => setCurrentCycleStart(e.target.value)}
              />
            </div>
            <div className="col-md-3">
              <label className="form-label">
                {t("CUSTOMER.COMMERCIAL_CONDITION.CURRENT_CYCLE_END")}
              </label>
              <input
                type="date"
                className="form-control"
                value={currentCycleEnd}
                onChange={(e) => setCurrentCycleEnd(e.target.value)}
              />
            </div>
          </div>

          <div className="d-flex justify-content-between align-items-center mt-4 pt-3 border-top">
            <div className="form-check">
              <input
                type="checkbox"
                className="form-check-input"
                id="billing-validated-by-client"
                checked={billingValidatedByClient}
                onChange={() => setBillingValidatedByClient((prev) => !prev)}
              />
              <label
                className="form-check-label"
                htmlFor="billing-validated-by-client"
              >
                {t("CUSTOMER.COMMERCIAL_CONDITION.BILLING_VALIDATED_BY_CLIENT")}
              </label>
            </div>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleSave}
              disabled={isSavingCondition}
            >
              {t("CUSTOMER.COMMERCIAL_CONDITION.SAVE")}
            </button>
          </div>
        </div>
      </div>

      {/* Condição comercial */}
      <div className="card mb-4">
        <div className="card-header bg-dark text-white fw-semibold mb-3">
          {t("CUSTOMER.COMMERCIAL_CONDITION.SELECT_CONDITION_TITLE")}
        </div>
        <div className="card-body">
          <div className="row g-3">
            <div className="col-md-4">
              <label className="form-label">
                {t("CUSTOMER.COMMERCIAL_CONDITION.TYPE")}
              </label>
              <select
                className="form-select"
                value={conditionType}
                onChange={(e) => setConditionType(e.target.value)}
                disabled={combosLoading}
              >
                <option value="">
                  {combosLoading
                    ? t("CUSTOMER.COMMERCIAL_CONDITION.LOADING_PLANS")
                    : t("CUSTOMER.COMMERCIAL_CONDITION.SELECT")}
                </option>
                {contractTypes.map((type) => (
                  <option key={type.contractTypeId} value={type.contractTypeId}>
                    {type.contractTypeName}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-4">
              <label className="form-label">
                {t("CUSTOMER.COMMERCIAL_CONDITION.PLAN_VALIDITY")}
              </label>
              <select
                className="form-select"
                value={planValidity}
                onChange={(e) => setPlanValidity(e.target.value)}
                disabled={combosLoading}
              >
                <option value="">
                  {t("CUSTOMER.COMMERCIAL_CONDITION.SELECT")}
                </option>
                {contractDurations.map((duration) => (
                  <option
                    key={duration.contractDurationId}
                    value={duration.contractDurationId}
                  >
                    {duration.contractDurationDescription}
                  </option>
                ))}
              </select>
            </div>
           {/* Sem campo direto em CommercialTermResponse (GAP 3) — manual */}
            <div className="col-md-4">
              <label className="form-label">
                {t("CUSTOMER.COMMERCIAL_CONDITION.NEXT_READJUSTMENT")}
              </label>
              <input
                type="text"
                className="form-control"
                value={nextReadjustment}
                onChange={(e) => setNextReadjustment(e.target.value)}
              />
            </div>
          </div>

          <div className="row g-3 mt-1">
            <div className="col-12">
              <label className="form-label">
                {t("COMMON.DESCRIPTION")}
              </label>
              <input
                type="text"
                className="form-control"
                value={conditionDescription}
                onChange={(e) => setConditionDescription(e.target.value)}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Serviço contratado */}
      <div className="card mb-4">
        <div className="card-header bg-dark text-white fw-semibold mb-3">
          {t("CUSTOMER.COMMERCIAL_CONDITION.CONTRACTED_SERVICE_TITLE")}
        </div>

        {featuresLoading && (
          <div className="card-body text-muted">
            {t("CUSTOMER.COMMERCIAL_CONDITION.LOADING_PLANS")}
          </div>
        )}

        {!featuresLoading && contractedFeatures.length === 0 && (
          <div className="card-header bg-dark text-white fw-semibold">
            {t("CUSTOMER.COMMERCIAL_CONDITION.SELECT_A_CONDITION_HINT")}
          </div>
        )}

        {!featuresLoading && contractedFeatures.length > 0 && (
          <>
            <ul className="list-group list-group-flush">
              {contractedFeatures.map((feature) => (
                <li
                  key={feature.featureId}
                  className="list-group-item d-flex justify-content-between align-items-center"
                >
                  <span>{feature.description}</span>
                  <div className="d-flex align-items-center gap-3">
                    <span className="text-muted" style={{ minWidth: "70px", textAlign: "right" }}>
                      R$ {(feature.value || 0).toFixed(2).replace(".", ",")}
                    </span>
                    <input
                      type="checkbox"
                      className="form-check-input"
                      checked={!!checkedFeatures[feature.featureId]}
                      onChange={() => toggleFeature(feature.featureId)}
                    />
                  </div>
                </li>
              ))}
            </ul>

            <div className="card-footer d-flex justify-content-end align-items-center gap-2">
              <span className="fw-bold">
                {t("CUSTOMER.COMMERCIAL_CONDITION.SUGGESTED_VALUE")}
              </span>
              <input
                type="text"
                className="form-control"
                style={{ maxWidth: "150px" }}
                value={suggestedValue.toFixed(2).replace(".", ",")}
                disabled
              />
            </div>
          </>
        )}
      </div>

      {/* Valores e formas de pagamento de serviços */}
      <div className="card mb-4">
        <div className="card-header bg-dark text-white fw-semibold mb-3">
          {t("CUSTOMER.COMMERCIAL_CONDITION.PAYMENT_VALUES_TITLE")}
        </div>
        <div className="card-body">
          <div className="row g-3">
            <div className="col-md-4">
              <label className="form-label">
                {t("CUSTOMER.COMMERCIAL_CONDITION.TOTAL_SERVICES")}
              </label>
              <input
                type="text"
                className="form-control"
                value={serviceValue}
                onChange={(e) => setServiceValue(e.target.value)}
              />
            </div>
            <div className="col-md-4">
              <label className="form-label">
                {t("CUSTOMER.COMMERCIAL_CONDITION.TOTAL_PLAN")}
              </label>
              <input
                type="text"
                className="form-control"
                value={serviceTotalPlanValue}
                onChange={(e) => setServiceTotalPlanValue(e.target.value)}
              />
            </div>
            <div className="col-md-4">
              <label className="form-label">
                {t("CUSTOMER.COMMERCIAL_CONDITION.PERIODICITY")}
              </label>
              {/* Sem endpoint de periodicidade confirmado — servicePeriodId
                  existe no schema mas não achamos um GET de combo pra ele. */}
              <input
                type="text"
                className="form-control"
                value={servicePeriodId}
                onChange={(e) => setServicePeriodId(e.target.value)}
              />
            </div>
          </div>

          <div className="row g-3 mt-1">
            <div className="col-md-4">
              <label className="form-label">
                {t("CUSTOMER.COMMERCIAL_CONDITION.MONTHLY_FEE")}
              </label>
              <input
                type="text"
                className="form-control"
                value={serviceMonthlyValue}
                onChange={(e) => setServiceMonthlyValue(e.target.value)}
              />
            </div>
            <div className="col-md-4">
              <label className="form-label">
                {t("CUSTOMER.COMMERCIAL_CONDITION.PAYMENT_METHOD")}
              </label>
              <select
                className="form-select"
                value={servicePaymentMethodId}
                onChange={(e) => setServicePaymentMethodId(e.target.value)}
                disabled={combosLoading}
              >
                <option value="">
                  {t("CUSTOMER.COMMERCIAL_CONDITION.SELECT")}
                </option>
                {paymentMethods.map((method) => (
                  <option key={method.paymentMethodId} value={method.paymentMethodId}>
                    {method.paymentMethodDescription}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-4">
              <label className="form-label">
                {t("CUSTOMER.COMMERCIAL_CONDITION.INSTALLMENTS")}
              </label>
              <select
                className="form-select"
                value={serviceInstallmentId}
                onChange={(e) => setServiceInstallmentId(e.target.value)}
                disabled={combosLoading}
              >
                <option value="">
                  {t("CUSTOMER.COMMERCIAL_CONDITION.SELECT")}
                </option>
                {installmentPlans.map((plan) => (
                  <option key={plan.installmentPlanId} value={plan.installmentPlanId}>
                    {plan.installmentPlanDescription}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <hr className="my-4" />

          {/* Valor de instalação */}
          <div className="fw-semibold mb-2 text-primary">
            {t("CUSTOMER.COMMERCIAL_CONDITION.INSTALLATION_VALUE_TITLE")}
          </div>
          <div className="row g-3">
            <div className="col-md-4">
              <label className="form-label">
                {t("CUSTOMER.COMMERCIAL_CONDITION.VALUE")}
              </label>
              <input
                type="text"
                className="form-control"
                value={installationValue}
                onChange={(e) => setInstallationValue(e.target.value)}
              />
            </div>
            <div className="col-md-4">
              <label className="form-label">
                {t("CUSTOMER.COMMERCIAL_CONDITION.PAYMENT_METHOD")}
              </label>
              <select
                className="form-select"
                value={installationPaymentMethodId}
                onChange={(e) => setInstallationPaymentMethodId(e.target.value)}
                disabled={combosLoading}
              >
                <option value="">
                  {t("CUSTOMER.COMMERCIAL_CONDITION.SELECT")}
                </option>
                {paymentMethods.map((method) => (
                  <option key={method.paymentMethodId} value={method.paymentMethodId}>
                    {method.paymentMethodDescription}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-4">
              <label className="form-label">
                {t("CUSTOMER.COMMERCIAL_CONDITION.INSTALLMENTS")}
              </label>
              <select
                className="form-select"
                value={installationInstallmentId}
                onChange={(e) => setInstallationInstallmentId(e.target.value)}
                disabled={combosLoading}
              >
                <option value="">
                  {t("CUSTOMER.COMMERCIAL_CONDITION.SELECT")}
                </option>
                {installmentPlans.map((plan) => (
                  <option key={plan.installmentPlanId} value={plan.installmentPlanId}>
                    {plan.installmentPlanDescription}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <hr className="my-4" />

          {/* Valor do equipamento */}
          <div className="fw-semibold mb-2 text-primary">
            {t("CUSTOMER.COMMERCIAL_CONDITION.EQUIPMENT_VALUE_TITLE")}
          </div>
          <div className="row g-3">
            <div className="col-md-4">
              <label className="form-label">
                {t("CUSTOMER.COMMERCIAL_CONDITION.VALUE")}
              </label>
              <input
                type="text"
                className="form-control"
                value={equipmentValue}
                onChange={(e) => setEquipmentValue(e.target.value)}
              />
            </div>
            <div className="col-md-4">
              <label className="form-label">
                {t("CUSTOMER.COMMERCIAL_CONDITION.PAYMENT_METHOD")}
              </label>
              <select
                className="form-select"
                value={equipmentPaymentMethodId}
                onChange={(e) => setEquipmentPaymentMethodId(e.target.value)}
                disabled={combosLoading}
              >
                <option value="">
                  {t("CUSTOMER.COMMERCIAL_CONDITION.SELECT")}
                </option>
                {paymentMethods.map((method) => (
                  <option key={method.paymentMethodId} value={method.paymentMethodId}>
                    {method.paymentMethodDescription}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-4">
              <label className="form-label">
                {t("CUSTOMER.COMMERCIAL_CONDITION.INSTALLMENTS")}
              </label>
              <select
                className="form-select"
                value={equipmentInstallmentId}
                onChange={(e) => setEquipmentInstallmentId(e.target.value)}
                disabled={combosLoading}
              >
                <option value="">
                  {t("CUSTOMER.COMMERCIAL_CONDITION.SELECT")}
                </option>
                {installmentPlans.map((plan) => (
                  <option key={plan.installmentPlanId} value={plan.installmentPlanId}>
                    {plan.installmentPlanDescription}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <hr className="my-4" />

          {/* Multas e visitas improdutivas */}
          <div className="fw-semibold mb-2 text-primary">
            {t("CUSTOMER.COMMERCIAL_CONDITION.FINES_TITLE")}
          </div>
          <div className="row g-3">
            <div className="col-md-4">
              <label className="form-label">
                {t("CUSTOMER.COMMERCIAL_CONDITION.CONTRACT_TERMINATION_FINE")}
              </label>
              <input
                type="text"
                className="form-control"
                value={fines.fineValue}
                onChange={handleFinesChange("fineValue")}
              />
            </div>
            <div className="col-md-4">
              <label className="form-label">
                {t("CUSTOMER.COMMERCIAL_CONDITION.TECHNICAL_VISIT")}
              </label>
              <input
                type="text"
                className="form-control"
                value={fines.visitValue}
                onChange={handleFinesChange("visitValue")}
              />
            </div>
            <div className="col-md-4">
              <label className="form-label">
                {t("CUSTOMER.COMMERCIAL_CONDITION.WITHDRAWAL")}
              </label>
              <input
                type="text"
                className="form-control"
                value={fines.removalValue}
                onChange={handleFinesChange("removalValue")}
              />
            </div>
          </div>

          <div className="row g-3 mt-1">
            <div className="col-md-4">
              <label className="form-label">
                {t("CUSTOMER.COMMERCIAL_CONDITION.UNPRODUCTIVE_VISIT")}
              </label>
              <input
                type="text"
                className="form-control"
                value={fines.unproductiveVisitValue}
                onChange={handleFinesChange("unproductiveVisitValue")}
              />
            </div>
            <div className="col-md-4">
              <label className="form-label">
                {t("CUSTOMER.COMMERCIAL_CONDITION.VEHICLE_EXCHANGE")}
              </label>
              <input
                type="text"
                className="form-control"
                value={fines.replacementValue}
                onChange={handleFinesChange("replacementValue")}
              />
            </div>
          </div>

          <div className="d-flex justify-content-end align-items-center gap-2 mt-4 pt-3 border-top">
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={handleClearPaymentValues}
            >
              {t("CUSTOMER.COMMERCIAL_CONDITION.CLEAR")}
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleSavePaymentValues}
              disabled={isSavingCondition}
            >
              {t("CUSTOMER.COMMERCIAL_CONDITION.SAVE")}
            </button>
          </div>
        </div>
      </div>

      {/* Condições comerciais cadastradas */}
      <div className="card mb-3">
        <div className="card-header bg-dark text-white fw-semibold">
          {t("CUSTOMER.COMMERCIAL_CONDITION.REGISTERED_CONDITIONS_TITLE")}
        </div>
        <div className="table-responsive">
          <table className="table mb-0 align-middle">
            <thead>
              <tr>
                <th>{t("CUSTOMER.COMMERCIAL_CONDITION.COLUMN_PLAN")}</th>
                <th>{t("CUSTOMER.COMMERCIAL_CONDITION.COLUMN_MONTHLY_FEE")}</th>
                <th>{t("CUSTOMER.COMMERCIAL_CONDITION.COLUMN_CONTRACTS_QTY")}</th>
                <th></th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {conditionsLoading && (
                <tr>
                  <td colSpan={5} className="text-muted">
                    {t("CUSTOMER.COMMERCIAL_CONDITION.LOADING_PLANS")}
                  </td>
                </tr>
              )}
              {!conditionsLoading && registeredConditions.length === 0 && (
                <tr>
                  <td colSpan={5} className="text-muted">
                    {t("CUSTOMER.COMMERCIAL_CONDITION.NO_CONDITIONS")}
                  </td>
                </tr>
              )}
              {!conditionsLoading &&
                registeredConditions.map((condition) => (
                  <tr
                    key={condition.commercialConditionId}
                    className={
                      condition.commercialConditionId === selectedConditionId
                        ? "table-active"
                        : ""
                    }
                  >
                    <td>{condition.description}</td>
                    <td>
                      {(condition.serviceMonthlyValue || 0)
                        .toFixed(2)
                        .replace(".", ",")}
                    </td>
                    <td>{condition.contractQuantity ?? "-"}</td>
                    <td>
                      <button
                        type="button"
                        className="btn btn-primary btn-sm"
                        onClick={() => handleEditCondition(condition)}
                      >
                        {t("CUSTOMER.COMMERCIAL_CONDITION.EDIT")}
                      </button>
                    </td>
                    <td>
                      <button
                        type="button"
                        className="btn btn-primary btn-sm"
                        disabled={isDeactivating}
                        onClick={() => setConditionToDelete(condition)}
                      >
                        {t("CUSTOMER.COMMERCIAL_CONDITION.DEACTIVATE")}
                      </button>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>

      <DeleteModal
        show={!!conditionToDelete}
        itemName={conditionToDelete?.description ?? ""}
        itemRemoveId={conditionToDelete?.commercialConditionId}
        handleClose={() => setConditionToDelete(null)}
        callBackRemove={confirmDeactivateCondition}
      />
    </div>
  );
}
